import { useEffect, useState } from 'react';

import type { TStepRunStatus } from '@/modules/automation/utils/step-run-status';

/** How long each step stays current while playback catches up with a run. */
export const RUN_STEP_PLAYBACK_MS = 600;

interface IUseRunStepPlaybackParams {
  /** Step numbers in document order. */
  stepNumbers: number[];
  getStepStatus?: (stepNumber: number) => TStepRunStatus | undefined;
  /** The run has started and is not over yet. */
  isRunActive: boolean;
  runId?: string;
}

interface IRunStepPlayback {
  /** The status to show for a step, held back until playback reaches it. */
  getShownStatus: (stepNumber: number) => TStepRunStatus | undefined;
  /** The step shown as current, or `undefined` before the run or once it is over. */
  currentStepNumber?: number;
}

const LIVE_STATUSES = ['failed', 'running', 'paused'] as const;

/** Index of the step the run is on; `stepNumbers.length` once every step is done. */
function findRunPosition(
  stepNumbers: number[],
  getStepStatus: IUseRunStepPlaybackParams['getStepStatus'],
  isRunActive: boolean,
): number | undefined {
  if (!getStepStatus) return undefined;
  const statuses = stepNumbers.map(stepNumber => getStepStatus(stepNumber));
  for (const status of LIVE_STATUSES) {
    const index = statuses.indexOf(status);
    if (index !== -1) return index;
  }
  if (statuses.includes('done')) {
    const firstPending = statuses.indexOf('pending');
    return firstPending === -1 ? statuses.length : firstPending;
  }
  return isRunActive ? 0 : undefined;
}

/**
 * Walks a test run forward one step at a time, so every step the run passes
 * through is shown as current for a moment even when the run outpaces the UI.
 * A run that is already known when it is focused is shown as it is, without
 * replaying it.
 */
export function useRunStepPlayback({
  stepNumbers,
  getStepStatus,
  isRunActive,
  runId,
}: IUseRunStepPlaybackParams): IRunStepPlayback {
  // Until the run is known to be live, "running" is only the status logic's
  // default guess (it assumes the first unfinished step is executing), so a
  // finished run whose state is still loading would flash a spinner.
  const readStatus = getStepStatus
    ? (stepNumber: number) => {
        const status = getStepStatus(stepNumber);
        return status === 'running' && !isRunActive ? undefined : status;
      }
    : undefined;
  const target = findRunPosition(stepNumbers, readStatus, isRunActive);
  const [shown, setShown] = useState<{ runId?: string; position?: number }>({
    runId,
    position: target,
  });

  let position = shown.position;
  const mustJump =
    shown.runId !== runId ||
    position === undefined ||
    target === undefined ||
    target < position;
  if (mustJump) {
    if (shown.runId !== runId || shown.position !== target) {
      setShown({ runId, position: target });
    }
    position = target;
  }

  // eslint-disable-next-line no-restricted-syntax -- reason: advances playback on a timer, which is not derivable state
  useEffect(() => {
    if (position === undefined || target === undefined || position >= target) {
      return;
    }
    const timer = window.setTimeout(() => {
      setShown(current => ({
        ...current,
        position: (current.position ?? 0) + 1,
      }));
    }, RUN_STEP_PLAYBACK_MS);
    return () => window.clearTimeout(timer);
  }, [position, target]);

  const getShownStatus = (stepNumber: number) => {
    const status = readStatus?.(stepNumber);
    if (position === undefined) return status;
    const index = stepNumbers.indexOf(stepNumber);
    if (index < position) return status;
    if (index === position) {
      if (position !== target) return 'running';
      if (!isRunActive) return status;
      return status === undefined || status === 'pending' ? 'running' : status;
    }
    return status === undefined ? undefined : 'pending';
  };

  return {
    getShownStatus,
    currentStepNumber:
      position !== undefined && position < stepNumbers.length
        ? stepNumbers[position]
        : undefined,
  };
}
